/**
 * RTK Query API slice for contracting packages management.
 * Types match the shapes defined in docs/cleantrade-wiki/contracting-api-contract.md.
 */
import { baseApi } from '@/store/api/baseApi';
import {
  ApiTags,
  ContractingPackageTagIds,
  PackageTagIds,
  TemplateTagIds,
} from '@/store/api/tags';

import { PRODUCT_TYPE } from '@/utils/enums';

import type { TemplateResponse } from './templatesApi';

// --- Types ---

export type ContractingProductType = `${PRODUCT_TYPE}`;

export interface PackageDetailResponse {
  id: number;
  name: string;
  description: string | null;
  productType: ContractingProductType;
  productTypeName: string;
  active: boolean;
  isDefault: boolean;
  templates: TemplateResponse[];
  created: string;
  updated: string;
}

/** One entry of `GET /contracting/admin/product-types`, read from the product rules catalog. */
export interface ContractingProductTypeResponse {
  value: ContractingProductType;
  name: string;
  description: string;
  /** Whether a template in a package of this product type may require regulatory data. */
  allowsRegulatoryData: boolean;
}

// --- Grouped response type for frontend consumption ---

export type PackagesByType = Record<
  ContractingProductType,
  PackageDetailResponse[]
>;

// --- Request types ---

export interface CreatePackageRequest {
  name: string;
  productType: ContractingProductType;
  description?: string;
}

export interface UpdatePackageRequest {
  id: number;
  productType: ContractingProductType;
  name?: string;
  description?: string;
  active?: boolean;
  isDefault?: boolean;
}

export interface DeletePackageRequest {
  id: number;
  productType: ContractingProductType;
}

export interface UploadTemplateRequest {
  packageId: number;
  productType: ContractingProductType;
  file: File;
  uploadAndSignatureRequired: boolean;
  requiresRegulatoryData: boolean;
}

// --- API Slice ---

const BASE_URL = '/api/v1/contracting/admin/packages';

export const packagesApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getPackages: build.query<PackagesByType, void>({
      query: () => ({ url: BASE_URL }),
      transformResponse: (response: PackageDetailResponse[]) => {
        const grouped: PackagesByType = {
          PHYSICAL_PPA: [],
          RECS: [],
          VIRTUAL_PPA: [],
          FULL_TOLL: [],
          TB_SWAP: [],
          FLOOR_UPSIDE_SHARE: [],
        };
        for (const pkg of response) {
          grouped[pkg.productType].push(pkg);
        }
        // Sort each group by id for stable render order across refetches
        for (const type of Object.keys(grouped) as ContractingProductType[]) {
          grouped[type].sort((a, b) => a.id - b.id);
          // Sort templates within each package by id so order doesn't shift on rename/update
          for (const pkg of grouped[type]) {
            pkg.templates.sort((a, b) => a.id - b.id);
          }
        }
        return grouped;
      },
      providesTags: [{ type: ApiTags.PACKAGES, id: PackageTagIds.LIST }],
    }),

    getContractingProductTypes: build.query<
      ContractingProductTypeResponse[],
      void
    >({
      query: () => ({ url: '/api/v1/contracting/admin/product-types' }),
    }),

    createPackage: build.mutation<PackageDetailResponse, CreatePackageRequest>({
      query: data => ({
        url: BASE_URL,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { productType }) => [
        { type: ApiTags.PACKAGES, id: PackageTagIds.LIST },
        {
          type: ApiTags.CONTRACTING_PACKAGES,
          id: `${ContractingPackageTagIds.LIST}-${productType}`,
        },
      ],
    }),

    updatePackage: build.mutation<PackageDetailResponse, UpdatePackageRequest>({
      query: ({ id, name, description, active, isDefault }) => ({
        url: `${BASE_URL}/${id}`,
        method: 'PUT',
        body: { name, description, active, isDefault },
      }),
      invalidatesTags: (result, error, { productType }) => [
        { type: ApiTags.PACKAGES, id: PackageTagIds.LIST },
        {
          type: ApiTags.CONTRACTING_PACKAGES,
          id: `${ContractingPackageTagIds.LIST}-${productType}`,
        },
      ],
    }),

    deletePackage: build.mutation<void, DeletePackageRequest>({
      query: ({ id }) => ({
        url: `${BASE_URL}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { productType }) => [
        { type: ApiTags.PACKAGES, id: PackageTagIds.LIST },
        {
          type: ApiTags.CONTRACTING_PACKAGES,
          id: `${ContractingPackageTagIds.LIST}-${productType}`,
        },
      ],
    }),

    uploadTemplate: build.mutation<TemplateResponse, UploadTemplateRequest>({
      queryFn: async (
        { packageId, file, uploadAndSignatureRequired, requiresRegulatoryData },
        _queryApi,
        _extraOptions,
        baseQuery
      ) => {
        // Step 1: Create template metadata
        const createResult = await baseQuery({
          url: `${BASE_URL}/${packageId}/templates`,
          method: 'POST',
          body: {
            name: file.name.replace(/\.docx$/i, ''),
            packageId,
            uploadAndSignatureRequired,
            requiresRegulatoryData,
          },
        });

        if (createResult.error) {
          return { error: createResult.error };
        }

        const template = createResult.data as TemplateResponse;

        // Step 2: Upload file as the first version
        const formData = new FormData();
        formData.append('file', file);

        const uploadResult = await baseQuery({
          url: `/api/v1/contracting/admin/templates/${template.id}/versions`,
          method: 'POST',
          body: formData,
        });

        if (uploadResult.error) {
          // A template with no version stops document generation for its whole package, so
          // remove the one step 1 created. Best effort: the upload error is the one to report.
          await baseQuery({
            url: `/api/v1/contracting/admin/templates/${template.id}`,
            method: 'DELETE',
          });
          return { error: uploadResult.error };
        }

        return { data: template };
      },
      invalidatesTags: (result, error, { productType }) => [
        { type: ApiTags.PACKAGES, id: PackageTagIds.LIST },
        { type: ApiTags.TEMPLATES, id: TemplateTagIds.LIST },
        {
          type: ApiTags.CONTRACTING_PACKAGES,
          id: `${ContractingPackageTagIds.LIST}-${productType}`,
        },
      ],
    }),
  }),
});

export const {
  useGetPackagesQuery,
  useCreatePackageMutation,
  useUpdatePackageMutation,
  useDeletePackageMutation,
  useUploadTemplateMutation,
  useGetContractingProductTypesQuery,
} = packagesApi;
